import React, { useState, useRef, useMemo, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { updateCommonInfo } from "../../../redux/commonSlice";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import "./style.css";
import "../common.css";
import {
  ActionCellRenderer,
  RoleCellRenderer,
  StatusRenderer,
  CompanyRenderer,
  SendEmailCellRenderer,
} from "../Common/CellRenderers/renderers";
import LoadingOverlay from "react-loading-overlay";
import ErrorContainer from "../../Common/Errors/ErrorContainer";
import {
  httpGetVendorProfileByEmail,
  httpGetManageUserDetails,
  httpGetConfigValues,
} from "../../../service/httpClient";
import { filterVendorProfileId } from "../../../utils/utils";
import * as Constants from "../../../constants/Constants";

function UserDetails() {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [updateStatus, setUpdateStatus] = useState("");
  const [errorStatus, setErrorStatus] = useState(false);
  const getOptions = (key, configData) => {
    let configValues = store?.configValues || [];
    configValues = configValues.length === 0 ? configData : configValues;
    const configValue = configValues.filter((item) => item.key === key);

    if (configValue && configValue.length > 0) {
      if (typeof configValue[0].value === "string") {
        return JSON.parse(configValue[0].value);
      }
      return configValue[0].value;
    }
    return [];
  };
  const [columnDefs, setColumnDefs] = useState([]);
  const dispatch = useDispatch();
  const store = useSelector(
    ({ service, common }) => ({
      profileId: common.profileId,
      configValues: common.configValues,
      roleName: service.roles?.roleName,
      merchandiseType:
        service.vendorProfile?.vendorDetail?.merchandiseType || "",
    }),
    shallowEqual
  );
  const getFormFieldsColumns = (configData) => {
    return [
      {
        field: "firstName",
      },
      {
        field: "lastName",
      },
      {
        field: "vmsRole",
        cellRenderer: RoleCellRenderer,
        cellRendererParams: {
          field: "vms",
          options: getOptions("vmsFOMRoles", configData) || [],
          updateManagerUsersErrors: updateManagerUsersErrors,
        },
        headerName: "Role",
      },
      {
        field: "applicationRole",
        cellRenderer: RoleCellRenderer,
        cellRendererParams: {
          field: "available",
          options: getOptions("availableFOMRoles", configData) || [],
          updateManagerUsersErrors: updateManagerUsersErrors,
        },
        headerName: "VSP Role",
        hide:
          store?.merchandiseType === Constants.VOB_MERCHANDISE_TYPE_MARKETPLACE
            ? true
            : false,
      },
      {
        field: "email",
      },
      {
        field: "status",
        cellRenderer: StatusRenderer,
        headerName: "Status",
      },
      {
        field: "vendorCorpName",
        cellRenderer: CompanyRenderer,
        headerName: "Company",
      },
      {
        field: "isActive",
        cellRenderer: ActionCellRenderer,
        cellRendererParams: {
          field: "isActive",
          updateManagerUsersErrors: updateManagerUsersErrors,
        },
      },
      {
        cellRenderer: SendEmailCellRenderer,
        cellRendererParams: {
          field: "sendEmail",
          updateManagerUsersErrors: updateManagerUsersErrors,
        },
      },
    ];
  };

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    suppressNavigable: true,
    cellClass: "no-border",
    cellStyle: { "justify-content": "center" },
  }));

  const loadUserDetailList = (vendorProfileId) => {
    setIsLoading(true);
    httpGetManageUserDetails(store.profileId || vendorProfileId)
      .then((rowData) => {
        const data = rowData?.data || [];
        setRowData(data);
        setErrorMessage("");
        setErrorStatus(false);
        setUpdateStatus(false);
        setIsLoading(false);
      })
      .catch((err) => {
        setRowData([]);
        setIsLoading(false);
        setErrorStatus(true);
        setUpdateStatus(false);
        setErrorMessage(err.message);
      });
  };

  const updateManagerUsersErrors = (status) => {
    if (status === "failure") {
      setIsLoading(false);
      setErrorStatus(true);
      setUpdateStatus(false);
      setErrorMessage(Constants.MANAGE_USER_ERROR_MSG);
    } else if (status === "success") {
      setIsLoading(false);
      setErrorStatus(false);
      setUpdateStatus(true);
      setSuccessMsg(Constants.MANAGE_USER_SUCCESS_MSG);
    }  else if (status === "email") {
      setIsLoading(false);
      setErrorStatus(false);
      setUpdateStatus(true);
      setSuccessMsg("Email sent successfully!");
    } else {
      setIsLoading(true);
      setErrorStatus(false);
      setUpdateStatus(false);
      setErrorMessage("");
    }
  };

  const fetchManageUserDetails = (configData) => {
    setColumnDefs(getFormFieldsColumns(configData));
    if (store?.profileId) {
      loadUserDetailList(store?.profileId);
    } else if (Cookie.get("userType") == Constants.VP_USER_TYPE_EXTERNAL) {
      httpGetVendorProfileByEmail(Cookie.get("email"))
        .then((res) => {
          const vendorProfileId = filterVendorProfileId(res.data || {});
          loadUserDetailList(vendorProfileId);
        })
        .catch((err) => console.log(err));
    } else if (localStorage.getItem("vid")) {
      loadUserDetailList(localStorage.getItem("vid"));
    } else {
      setIsLoading(false);
      setErrorMessage(Constants.VOB_NO_VENDOR_SELECTED_MSG);
      setErrorStatus(true);
      setUpdateStatus(false);
    }
  };
  useEffect(() => {
    if (store?.configValues?.length === 0) {
      httpGetConfigValues()
        .then((response) => {
          dispatch(
            updateCommonInfo({
              configValues: response.data,
            })
          );
          fetchManageUserDetails(response.data);
        })
        .catch((err) => console.log(err));
    } else {
      fetchManageUserDetails();
    }
  }, []);

  return (
    <LoadingOverlay active={isLoading} spinner>
      <div className="m-top-10">
        <ErrorContainer
          errorMsg={errorMessage}
          updateStatus={updateStatus}
          successMsg={successMsg}
          errorStatus={errorStatus}
          errorStyle="errorMessage"
          successStyle="umSuccessMsg"
        />
      </div>
      <div className="ag-theme-alpine manageuser-grid-container">
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows={true}
          rowSelection="multiple"
          overlayNoRowsTemplate={
            rowData?.length > 0 ? "" : Constants.NO_DATA_AVAILABLE
          }
        />
      </div>
    </LoadingOverlay>
  );
}

export default UserDetails;
